import {Box, BoxProps, LinearProgress, Typography} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {FC, useState, useCallback, useEffect, useRef, useContext} from "react";
import {mobileNavBarHeight, palette} from "@/constants";
import {IconUpArrow} from "@/icons/IconUpArrow";
import {useMatch, useNavigate, useLocation} from "react-router-dom";
import {useGetCourseDetails, useGetCourseCurriculum} from "@/app2/shared/data";
import {LocaleContext, resolveLocale} from "@/app2/shared/contexts";
import {useMq} from "@buildwitt/shapeshift-common/v1/hooks";
import {CourseCurriculumModule} from "@/app2/components/CourseCurriculumModule";
import {useSwipe} from "@buildwitt/shapeshift-common/v1/hooks";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import {CourseProgressionSummary} from "./CourseProgressionSummary";
import {TrainingFeedSideMenuContent} from "./TrainingFeedSideMenuContent";

interface MobileTrainingFeedDrawerProps extends Omit<BoxProps, "children"> {
	courseSlug: string;
}

const useStyles = makeStyles<{ open: boolean; isNewHomeScreen: boolean }>()((theme, { open, isNewHomeScreen }) => ({
	mobileTrainingFeedDrawer: {
		position: "fixed",
		top: open ? "20dvh" : "calc(100dvh - 100px)",
		transition: "top ease-out .3s",
		minHeight: "calc(100% - 20dvh)",
		width: "100%",
		background: palette.drawer,
		[theme.breakpoints.up("sm")]: {
			display: "none",
		},
	},
	openDrawerIconContainer: {
		background: "linear-gradient(180deg, rgba(44,44,44,0) 0%, rgba(44,44,44,0) 50%, rgba(44,44,44,1) 51%, rgba(44,44,44,1) 100%)",
		maxHeight: "47px",
		overflow: "hidden",
		zIndex: 10,
		width: "100%",
		position: "absolute",
		height: "50px",
		top: "-25px",
		display: "flex",
		justifyContent: "center",
	},
	openDrawerIcon: {
		zIndex: 10,
		justifyContent: "center",
		display: "flex",
		transition: "transform .5s ease-out",
		transform: open ? "rotate(180deg)" : "rotate(0)",
		width: "22px",
		height: "22px",
		borderRadius: "100px",
		background: palette.drawer,
		padding: "18px",
		"& svg": {
			transition: "top .5s ease-out",
			position: "relative",
			top: open ? "15px" : "0px",
		},
		"& path": {
			fill: palette.drawer,
		},
	},
	content: {
		maxHeight: `calc(80vh - ${mobileNavBarHeight})`,
		overflowY: open ? "auto" : "hidden",
		paddingBottom: "60px",
		boxSizing: "border-box",
	},
	sideMenu: {
		"[class*='moduleTitle']": {
			color: "white",
		},
		borderRadius: "16px",
		margin: "15px",
		padding: isNewHomeScreen ? 0 : "15px 25px",
		background: "#282828",
	},
	moduleItem: {
		margin: "10px 0",
	},
}));

/**
 * Mobile Training Feed Drawer
 * 
 * Swipeable drawer for mobile that shows:
 * - Training feed
 * - Course curriculum
 * 
 * Uses React Query instead of CurriculumContext.
 */
export const MobileTrainingFeedDrawer: FC<MobileTrainingFeedDrawerProps> = ({
	courseSlug,
	className,
	...props
}) => {
	const [open, setOpen] = useState(false);
	const { newHomeScreen } = useDevCycleSdk();
	const { classes, cx } = useStyles({ open, isNewHomeScreen: newHomeScreen });
	const contentRef = useRef<HTMLDivElement>(null);
	const location = useLocation();
	const pageIsFeed = useMatch("/feed/:courseSlug/*");
	const navigate = useNavigate();
	const mq = useMq();

	useEffect(() => {
		if (contentRef.current?.scrollTop) contentRef.current.scrollTop = 0;
	}, [open]);

	useEffect(() => {
		setOpen(false);
	}, [location]);

	const [{locale}] = useContext(LocaleContext);
	const detailsParams = {locale: resolveLocale(locale)};
	const curriculumParams = {locale: resolveLocale(locale)};

	// Fetch course data using React Query
	const {data: courseData} = useGetCourseDetails(courseSlug, detailsParams);
	const {data: curriculumData, isLoading: isLoadingCurriculum} = useGetCourseCurriculum(courseSlug, curriculumParams, {
		enabled: !!courseData?.course?.enrollment
	});

	const course = courseData?.course;
	const curriculum = curriculumData;

	// Swipe gestures
	const {handleTouchStart, handleTouchEnd} = useSwipe({
		threshold: 50,
		onSwipeUp: () => {
			if (!open) setOpen(true);
		},
		onSwipeDown: () => {
			if (open) setOpen(false);
		}
	});

	const handleModuleClick = useCallback((item: any) => {
		if (item.isLocked) {
			// TODO: Show notification that module is locked
			return;
		}

		navigate(`/course/${courseSlug}/${item.type}/${item.slug}`);
		setOpen(false); // Close drawer on mobile after navigation
	}, [courseSlug, navigate]);

	// Don't render on desktop
	if (!mq.mobile) {
		return null;
	}

	// On mobile, show only the content for the current route (no tab bar).
	// From /feed/... → Today's Learning only. From /course/... → Course Content only.
	const showFeedContent = !!pageIsFeed;

	return (
		<Box className={cx(classes.mobileTrainingFeedDrawer, className)} {...props}>
			{/* Drawer handle – swipe on handle like legacy */}
			<Box
				className={classes.openDrawerIconContainer}
				onTouchStart={handleTouchStart}
				onTouchEnd={handleTouchEnd}
			>
				<Box className={classes.openDrawerIcon} onClick={() => setOpen((prev) => !prev)}>
					<IconUpArrow />
				</Box>
			</Box>

			{/* Content – single view based on route, no tabs on mobile */}
			{/* Feed: content only (no sideMenu wrapper), matching legacy */}
			{showFeedContent && (
				<TrainingFeedSideMenuContent ref={contentRef} className={classes.content} />
			)}

			{/* Course: content + sideMenu like legacy CommonCourseSideMenuContent */}
			{!showFeedContent && (
				<Box ref={contentRef} className={cx(classes.content, classes.sideMenu)}>
						{/* Course progress summary */}
						{course?.enrollment && curriculum && (
							<CourseProgressionSummary
								title={course.title}
								percentComplete={curriculum.progressPercent}
								completedModules={curriculum.completedItems}
								totalModules={curriculum.totalItems}
							/>
						)}

						{/* Loading state */}
						{isLoadingCurriculum && (
							<Box py={2}>
								<LinearProgress />
							</Box>
						)}

						{/* Curriculum list */}
						{curriculum && curriculum.items.length > 0 ? (
							<Box mt={2} sx={{paddingTop: "12px"}}>
								{curriculum.items.map((item) => (
									<CourseCurriculumModule
										key={item.id}
										courseSlug={courseSlug}
										module={item}
										onClick={() => handleModuleClick(item)}
										className={classes.moduleItem}
									/>
								))}
							</Box>
						) : !isLoadingCurriculum && (
							<Box py={2}>
								<Typography variant="body2" color="text.secondary">
									No curriculum items available.
								</Typography>
							</Box>
						)}
				</Box>
			)}
		</Box>
	);
};

